import { useHandler } from "@redotech/react-util/hook";
import { PageContext } from "@redotech/redo-model/agents/page-context";
import { RedoInternalProduct } from "@redotech/redo-model/redo-internal-products";
import { ToolComponentProps } from "@redotech/redo-web/ai/chat/types/tool-component-props";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { nativeRandom, shuffle } from "@redotech/util/random";
import {
  createContext,
  type ReactNode,
  useContext,
  useMemo,
  useState,
} from "react";
import { getActiveProductNames } from "./active-products";

export type AskRedoViewMode = "slide-panel" | "page-takeover";
export type AskRedoPageContext = PageContext & {
  viewMode?: AskRedoViewMode;
  badgeText?: string;
};

export interface AgentToolComponents {
  [key: string]: ((args: ToolComponentProps) => ReactNode) | undefined;
}

export interface AgentToolResultHandlers {
  [key: string]: ((result: unknown) => void) | undefined;
}

export interface GlobalAgentContextType {
  pageContext?: PageContext;
  setPageContext: (context: AskRedoPageContext | undefined) => void;
  askRedoViewMode: AskRedoViewMode;
  setAskRedoViewMode: (viewMode: AskRedoViewMode) => void;
  askRedoBadgeText?: string;
  askRedoAttentionSignal: number;
  requestAskRedoAttention: () => void;
  defaultSuggestedQuestions: string[];
  onPageDataChanged?: () => void;
  setOnPageDataChanged: (callback: (() => void) | undefined) => void;
  /**
   * Tool components contributed by the currently mounted page. Pages call
   * setToolComponents on mount and reset to undefined on unmount so the
   * global Ask Redo chat can render page-specific tools.
   */
  toolComponents?: AgentToolComponents;
  setToolComponents: (components: AgentToolComponents | undefined) => void;
  toolResultHandlers?: AgentToolResultHandlers;
  setToolResultHandlers: (
    handlers: AgentToolResultHandlers | undefined,
  ) => void;
}

export const GlobalAgentContext = createContext<GlobalAgentContextType>({
  pageContext: undefined,
  setPageContext: () => {},
  askRedoViewMode: "slide-panel",
  setAskRedoViewMode: () => {},
  askRedoBadgeText: undefined,
  askRedoAttentionSignal: 0,
  requestAskRedoAttention: () => {},
  defaultSuggestedQuestions: [],
  onPageDataChanged: undefined,
  setOnPageDataChanged: () => {},
  toolComponents: undefined,
  setToolComponents: () => {},
  toolResultHandlers: undefined,
  setToolResultHandlers: () => {},
});

export interface GlobalAgentProviderProps {
  children: React.ReactNode;
}

export function GlobalAgentProvider({ children }: GlobalAgentProviderProps) {
  const [pageContext, setPageContext] = useState<PageContext | undefined>(
    undefined,
  );
  const [askRedoViewMode, setAskRedoViewMode] =
    useState<AskRedoViewMode>("slide-panel");
  const [askRedoBadgeText, setAskRedoBadgeText] = useState<string | undefined>(
    undefined,
  );
  const [askRedoAttentionSignal, setAskRedoAttentionSignal] = useState(0);
  const [onPageDataChanged, setOnPageDataChanged] = useState<
    (() => void) | undefined
  >(undefined);
  const [toolComponents, setToolComponents] = useState<
    AgentToolComponents | undefined
  >(undefined);
  const [toolResultHandlers, setToolResultHandlers] = useState<
    AgentToolResultHandlers | undefined
  >(undefined);

  const defaultSuggestedQuestions = useDefaultSuggestedQuestions();

  const stableSetOnPageDataChanged = useHandler(
    (cb: (() => void) | undefined) => setOnPageDataChanged(() => cb),
  );
  const setAskRedoPageContext = useHandler(
    (context: AskRedoPageContext | undefined) => {
      const { badgeText, viewMode, ...agentPageContext } = context ?? {};
      setAskRedoViewMode(viewMode ?? "slide-panel");
      setAskRedoBadgeText(badgeText);
      setPageContext(context ? agentPageContext : undefined);
    },
  );
  const requestAskRedoAttention = useHandler(() => {
    setAskRedoAttentionSignal((signal) => signal + 1);
  });

  const contextValue = useMemo(
    () => ({
      pageContext,
      setPageContext: setAskRedoPageContext,
      askRedoViewMode,
      setAskRedoViewMode,
      askRedoBadgeText,
      askRedoAttentionSignal,
      requestAskRedoAttention,
      defaultSuggestedQuestions,
      onPageDataChanged,
      setOnPageDataChanged: stableSetOnPageDataChanged,
      toolComponents,
      setToolComponents,
      toolResultHandlers,
      setToolResultHandlers,
    }),
    [
      pageContext,
      setAskRedoPageContext,
      askRedoViewMode,
      askRedoBadgeText,
      askRedoAttentionSignal,
      requestAskRedoAttention,
      defaultSuggestedQuestions,
      onPageDataChanged,
      stableSetOnPageDataChanged,
      toolComponents,
      toolResultHandlers,
    ],
  );

  return (
    <GlobalAgentContext.Provider value={contextValue}>
      {children}
    </GlobalAgentContext.Provider>
  );
}

/**
 * Resolves the suggested-questions list a chat surface should show:
 * page-specific questions when the active page set any, otherwise
 * the team-level defaults.
 *
 * Empty-array semantics are intentional. Pages typically compute
 * `pageContext.suggestedQuestions` dynamically by conditional spreads
 * over page state (see e.g. `return-details.tsx` building the list
 * from a return's status flags) — a future page that filters down to
 * `[]` should degrade to the team defaults, NOT silently show
 * nothing. If we ever need explicit "no suggestions" we'll add a
 * separate field on `PageContext`.
 *
 * Single source of truth: legacy `HelpChatGlobal`, the new Ask Redo
 * page, and the Ask Redo slide-out all read from here.
 */
export function useResolvedSuggestedQuestions(): string[] {
  const { pageContext, defaultSuggestedQuestions } =
    useContext(GlobalAgentContext);
  return useMemo(() => {
    const fromPage = pageContext?.suggestedQuestions ?? [];
    return fromPage.length > 0 ? fromPage : defaultSuggestedQuestions;
  }, [pageContext?.suggestedQuestions, defaultSuggestedQuestions]);
}

function useDefaultSuggestedQuestions(): string[] {
  const team = useContext(TeamContext);

  return useMemo(() => {
    const activeProducts = getActiveProductNames(team?.redoInternalProducts);

    const suggestedQuestions: string[] = [];

    if (activeProducts.includes(RedoInternalProduct.RETURNS)) {
      suggestedQuestions.push(
        "What were my most returned products over the last 90 days?",
        "What were the most common return reasons last month?",
      );
    }
    if (activeProducts.includes(RedoInternalProduct.PACKAGE_PROTECTION)) {
      suggestedQuestions.push(
        "What percentage of my orders are eligible for package protection?",
        "How many orders had package protection last week?",
      );
    }

    return shuffle(suggestedQuestions, nativeRandom).slice(0, 3);
  }, [team?.redoInternalProducts]);
}
